/**
 * The requester portal shell — Step 71. Anatomy extrapolated from the mined
 * portal captures (Kai's ruling: "follow the design language from the
 * portal"): a branded header with plain nav tabs (Home / My requests), a
 * prominent "New request" action, and the signed-in person's menu — no agent
 * chrome, no sidebar. Colours come from our own tokens with the tenant's
 * --brand slot, exactly like the agent app.
 */
import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, LayoutGrid, LogOut, Plus, TriangleAlert } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/AuthContext';
import { useBranding, useDocumentTitle } from '../../lib/useBranding';
import { BrandMark } from '../../components/shell/BrandMark';
import { InstallApp } from '../../components/shell/InstallApp';
import { PortalAnnouncementBanner } from '../../features/announcements/PortalAnnouncementBanner';

export function PortalShell() {
  useDocumentTitle();
  const { me, signOut } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [newOpen, setNewOpen] = useState(false);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Step 140 — the first Tab stop on every portal page. Visually hidden
          until focused, then a real button-shaped link; it jumps past the
          header and nav to the page's content. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[60] focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:text-primary-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        Skip to content
      </a>
      <header className="safe-top safe-x border-b bg-card">
        {/* Step 141 — the requester is more likely to be on a phone than not.
            The header WRAPS: brand and actions on the first line, the nav on a
            second full-width line below md, with no JavaScript and nothing
            hidden behind a menu — every destination stays one tap away. */}
        <div className="mx-auto flex min-h-14 w-full max-w-5xl flex-wrap items-center gap-x-4 gap-y-1 px-4 py-1.5 md:flex-nowrap md:py-0">
          <NavLink to="/portal" className="flex items-center gap-2">
            <BrandMark />
          </NavLink>
          <nav
            aria-label="Portal"
            className="order-last flex w-full items-center gap-1 overflow-x-auto pb-1 md:order-none md:ml-4 md:w-auto md:overflow-visible md:pb-0"
          >
            {(
              [
                ['/portal', 'Home'],
                ['/portal/help', 'Help articles'],
                ['/portal/requests', 'My requests'],
                // Step 47 — a line manager's whole reason to be here. Shown
                // to everyone rather than gated on "do they have any": an
                // empty page that says so is better than a nav item that
                // appears and disappears depending on somebody else's week.
                ['/portal/approvals', 'Approvals'],
              ] as const
            ).map(([to, label]) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/portal'}
                className={({ isActive }) =>
                  cn(
                    'rounded-md px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isActive
                      ? 'bg-accent font-medium text-accent-foreground'
                      : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                  )
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex-1" />
          {/* The reference's two-entry New menu (Kai): Report an issue →
              INCIDENT; Request a service → SERVICE REQUEST. */}
          <div className="relative">
            <Button size="sm" onClick={() => setNewOpen((o) => !o)}>
              <Plus /> New <ChevronDown className="h-3.5 w-3.5" />
            </Button>
            {newOpen && (
              <div className="fixed inset-x-2 top-14 z-30 max-h-[70vh] overflow-y-auto rounded-md border bg-popover py-1 shadow-md sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-1 sm:max-h-none sm:w-64 sm:overflow-hidden">
                <button
                  className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-accent"
                  onClick={() => {
                    setNewOpen(false);
                    navigate('/portal/report');
                  }}
                >
                  <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                  <span>
                    <span className="block text-sm font-medium">Report an issue</span>
                    <span className="block text-xs text-muted-foreground">
                      Something isn't working — raise a ticket
                    </span>
                  </span>
                </button>
                <button
                  className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-accent"
                  onClick={() => {
                    setNewOpen(false);
                    navigate('/portal/catalogue');
                  }}
                >
                  <LayoutGrid className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                  <span>
                    <span className="block text-sm font-medium">Request a service</span>
                    <span className="block text-xs text-muted-foreground">
                      Raise a request from the catalogue
                    </span>
                  </span>
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Staff viewing their own portal get a way back; a requester
                never sees this because there is nowhere for them to go. */}
            {me && me.user.kind !== 'requester' && (
              <button
                onClick={() => navigate('/')}
                title="Agent workspace"
                className="flex items-center gap-1 rounded text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {/* Step 187 — the label STAYS on a phone. Step 183 collapsed
                    it to a bare arrow to save room, and Kai couldn't find it:
                    an unlabelled grey arrow reads as "back", not "go to the
                    agent workspace". Sign out survives as an icon because a
                    door-and-arrow is universally that; this is an app-specific
                    destination and has to say its name. The header wraps if it
                    must — a visible control on two lines beats a hidden one. */}
                <ArrowLeft className="h-3.5 w-3.5 shrink-0" />
                <span>Agent workspace</span>
              </button>
            )}
            <button
              onClick={() => navigate('/portal/profile')}
              className="hidden max-w-40 truncate rounded text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:block"
            >
              {me?.user.name ?? me?.user.email}
            </button>
            <button
              onClick={async () => {
                const staff = me?.user.kind !== 'requester';
                await signOut();
                // Send people back to the door they actually came through —
                // a staff member bounced to the portal's magic-link page
                // would have no way to sign back in.
                window.location.assign(staff ? '/login' : '/portal/login');
              }}
              title="Sign out"
              className="flex items-center gap-1 rounded text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {/* Step 186 — an icon on a phone, where the right cluster is
                  tightest; the label returns at lg. The door-and-arrow reads as
                  sign out on its own. */}
              <LogOut className="h-3.5 w-3.5 shrink-0" />
              <span className="hidden lg:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>
      <main
        id="main"
        tabIndex={-1}
        className={cn(
          'mx-auto w-full flex-1 px-4 py-6 focus:outline-none',
          // Help articles carry screenshots authored at up to ~900px; the
          // 5xl reading column squeezed them. Everything else keeps it.
          pathname.startsWith('/portal/help') ? 'max-w-6xl' : 'max-w-5xl',
        )}
      >
        {/* Inside the content column, above the page's own heading — Kai:
            *"maybe it can be within the page for the requester portal end"*.
            A full-bleed strip over a branded header reads as an outage. */}
        <PortalAnnouncementBanner />
        {/* Step 175 — the portal is where "add it to my phone" belongs: these
            are the people who would keep it on a home screen, and they are the
            ones on a phone. It renders nothing once installed, and nothing in a
            browser that cannot install. */}
        <InstallApp />
        <Outlet />
      </main>
      <footer className="border-t py-4 text-center text-xs text-muted-foreground">
        {branding?.name ?? ''}
      </footer>
    </div>
  );
}
